import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NavigationService } from '../../../Layout/navigation.service';
import { YearPickerMixin } from '../../../UI-Lib/Mixins';
import { ChooseTermPopoverComponent } from '../../../Common/Contracts-Invoices/choose-term-popover.component';
import { ContractsInvoicesService } from '../../../Common/Contracts-Invoices/contract-invoices.service';
import { ContractTerm, ContractTermData, Engagement } from '../../../Common/Contracts-Invoices/contract-invoices.model';
import { Constants, ContractServiceType } from '../../../constants.new';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { EngagementNumberLookupModalComponent } from '../../../Common/Contracts-Invoices/engagement-number-lookup-modal.component';
import { ToastrService } from 'ngx-toastr';
import { Decimal } from 'decimal.js';
import { RestrictService, Roles } from '../../../Common/Permissions/restrict.service';
import { TimerService } from '../../../UI-Lib/Utilities';
import { CommentsModalService } from '../../../Comments/commentsModal.service';
import { FeatureFlagsService } from '../../../Common/FeatureFlags/feature-flags-service';
import { TermRatesAndFeesComponent } from '../../../Common/Contracts-Invoices/term-rates-and-fees.component';
import { CurrencyMaskModule } from '../../../UI-Lib/Currency/Mask/currencyMask.module';
import {
    MessageModalButtons,
    MessageModalDisplayMode,
    MessageModalService,
} from '../../../UI-Lib/Message-Box/messageModal.service';
import { UIRouter } from '@uirouter/angular';
import { BreadCrumb, BreadCrumbsComponent } from '../../../UI-Lib/Bread-Crumb/breadCrumbs.component';
import { CompanyService } from '../company.service';
import { AceLoaderComponent } from '../../../UI-Lib/ACE/Loader/loader.component';
import { WeissmanModalService } from '../../../Compliance/WeissmanModalService';
import { AddTermYearComponent, AddTermYearModalParams } from './Add-Term-Year/addTermYear.component';
import {
    ConfigureFeeEscalatorComponent,
    ConfigureFeeEscalatorModalParams,
} from './Configure-Fee-Escalator/configureFeeEscalator.component';
import { HelpModule } from '../../../UI-Lib/Help-Tooltip/help.module';

export interface ServiceInfo {
    id: number;
    name: string;
    fullDisplayName?: string;
}

interface PropertyType {
    name: string;
    propertyTypeId: number;
}

@Component({
    selector: 'contract-terms-panel-company',
    templateUrl: './contractTermsCompany.component.html',
    styleUrls: ['./contractTermsCompany.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        CurrencyMaskModule,
        TermRatesAndFeesComponent,
        ChooseTermPopoverComponent,
        BreadCrumbsComponent,
        AceLoaderComponent,
        HelpModule,
    ],
})
export class ContractTermsCompanyComponent extends YearPickerMixin() implements OnInit, OnDestroy {
    constructor(
        private readonly _contractsInvoicesService: ContractsInvoicesService,
        private readonly _messageModalService: MessageModalService,
        private readonly _modalService: BsModalService,
        private readonly _toastr: ToastrService,
        private readonly _restrictService: RestrictService,
        private readonly _commentsModalService: CommentsModalService,
        private readonly _constants: Constants,
        private readonly _timer: TimerService,
        private readonly _featureFlagService: FeatureFlagsService,
        private readonly _navigationService: NavigationService,
        private readonly _companyService: CompanyService,
        private readonly _weissmanModalService: WeissmanModalService,
        private readonly _router: UIRouter,
    ) {
        super();
    }

    companyId: number;
    isTopLevel: boolean;

    breadcrumbs: BreadCrumb[];

    isLoading: boolean = false;
    hasViewPermission: boolean = false;
    hasEditPermission: boolean = false;
    isEditMode: boolean = false;
    editLock: boolean;
    topLevelYears: number[] = [];
    subsidiaryYears: number[] = [];
    selectedYear: number;
    propertyTypes: PropertyType[];
    allData: ContractTermData;
    topLevelCompanyContractTerms: ContractTerm[];
    currentContractTerms: ContractTerm[];
    useTopLevelCompanyTerms: boolean = false;
    bsModalRef: BsModalRef;
    recentYears: number[];
    currentYear: number = new Date().getFullYear();
    services: ServiceInfo[];
    ContractServiceType = ContractServiceType;

    private _destroy$: Subject<void> = new Subject();

    async ngOnInit(): Promise<void> {
        this.hasViewPermission = this._restrictService.isInRole(Roles.RYANPRIVATEITEMSVIEW);
        this.hasEditPermission = this._restrictService.isInRole(Roles.RYANPRIVATEITEMSEDIT);

        this.companyId = +this._router.globals.params['companyId'];

        if (!(this.hasEditPermission || this.hasViewPermission)) {
            this._router.stateService.go('company', { companyId: this.companyId });
        }

        // getCompany checks the cache first, so no extra call if already loaded
        const company = await this._companyService.getCompany(this.companyId);
        this.isTopLevel = !company.parentId;

        this.breadcrumbs = [{ name: company.companyName, target: 'company', options: { companyId: this.companyId } }];

        this.services = Object.values(this._constants.ContractServiceTypes).sort((a: ServiceInfo, b: ServiceInfo) =>
            a.name.localeCompare(b.name),
        );


        this._navigationService.globalEditMode$.pipe(takeUntil(this._destroy$)).subscribe((editMode) => {
            this.editLock = editMode;
        });

        this.loadPanel();
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    loadPanel(): void {
        this.propertyTypes = Object.entries(this._constants.PropertyTypeNames).map(([id, name]) => {
            return {
                name: name as string,
                propertyTypeId: Number(id),
            };
        });

        this.recentYears = Array.from({ length: 16 }, (_, index) => this.currentYear + 5 - index);

        this.isLoading = true;
        this._contractsInvoicesService.getContractTermsByCompany(this.companyId).then((result: ContractTermData) => {
            this._populateContractTermData(result);
            this.isLoading = false;
        });
    }

    goToEditMode(): void {
        this.isEditMode = true;
        this._navigationService.enableNavWarning('You have unsaved changes. Are you sure you want to leave this page?');
    }

    addTerm(): void {
        const newTerm: ContractTerm = {
            contractTermDetailId: null,
            contractServiceTypeId: this.services[0]?.id,
            contractServiceTypeName: this.services[0]?.name,
            propertyTypeId: null,
            propertyTypeName: null,
            taxYear: this._getSelectedYear(this.subsidiaryYears),
            engagement: '',
            siteDefault: true,
            contingencyPct: null,
            contingencyCap: null,
            flatFee: null,
            hourlyRate: null,
            remarks: null,
            rowVersion: '',
            contractTermRowVersion: '',
            efAction: 'add',
        };

        this.currentContractTerms.push(newTerm);
    }

    async yearChanged(): Promise<void> {
        const oldVal = this.selectedYear;

        if (this._areTermsDirty()) {
            try {
                await this._messageModalService.open({
                    message: 'Discard changes for current changes and switch year selection?',
                    buttons: MessageModalButtons.OKCancel,
                    displayMode: MessageModalDisplayMode.SingleMessage,
                });
                this._getDataForYear();
            } catch {
                this._timer.setTimeout(() => {
                    this.selectedYear = oldVal;
                });
            }
        } else {
            this._timer.setTimeout(() => {
                this._getDataForYear();
            });
        }
    }

    removeTerm(term: ContractTerm, index: number): void {
        if (term.efAction === 'add') {
            this.currentContractTerms.splice(index, 1);
        } else {
            term.efAction = 'delete';
        }
    }

    async rollForwardYear(): Promise<void> {
        if (await this._confirmAutoSave()) {
            await this._launchAddYearModal();
        }
    }

    async configureFeeEscalator(): Promise<void> {
        if (await this._confirmAutoSave()) {
            await this._launchConfigureFeeEscalatorModal();
        }
    }

    cancelEdit(): void {
        this._getDataForYear();

        this.isEditMode = false;
        this._navigationService.disableNavWarning();
    }

    async savePanel(stayInEditMode?: boolean): Promise<void> {
        const errorMessage = this._panelIsInvalid();
        if (errorMessage) {
            this._toastr.error('Error!', errorMessage);
            return;
        }

        const saveBody = {
            useTopLevelCompanyTerms: this.useTopLevelCompanyTerms,
            ContractTerms: this.useTopLevelCompanyTerms
                ? this.topLevelCompanyContractTerms
                : this._getCurrentTermsForSave(),
            efAction: this.useTopLevelCompanyTerms !== this.allData.useTopLevelCompanyTerms ? 'update' : null,
            companyRowVersion: this.allData.companyRowVersion,
        };

        this.isLoading = true;
        try {
            const requireConfirmationDialog = await this._contractsInvoicesService.shouldRequireConfirmationDialog(
                this.companyId,
                saveBody,
            );

            if (requireConfirmationDialog) {
                try {
                    await this._messageModalService.open({
                        message: 'Overrides will be removed',
                        buttons: MessageModalButtons.OKCancel,
                        displayMode: MessageModalDisplayMode.SingleMessage,
                    });
                    await this._saveData(saveBody, stayInEditMode);
                } catch {
                    this.cancelEdit();
                }
            }

            return this._saveData(saveBody, stayInEditMode);
        } finally {
            this.isLoading = false;
        }
    }

    lookupEngagement(term: ContractTerm): void {
        this.bsModalRef = this._modalService.show(EngagementNumberLookupModalComponent, { class: 'modal-lg' });
        this.bsModalRef.content.title = 'Lookup Engagement Number';

        this.bsModalRef.content.onClose = (engagement: Engagement) => {
            term.engagement = engagement.engagementID;
        };
    }

    termsAreEditable(): boolean {
        return this.isEditMode && !this.useTopLevelCompanyTerms;
    }

    showRollForwardYearButton(): boolean {
        return (
            this.termsAreEditable() &&
            this.selectedYear !== 0 &&
            this.currentContractTerms.some((term: ContractTerm) => {
                return !!term.contractTermDetailId && term.efAction !== 'delete';
            })
        );
    }

    useTopLevelCompanyChanged(): void {
        this._setSelectedYear();
        this._getDataForYear();
    }

    serviceTypeChanged(newServiceType: string, term: ContractTerm): void {
        const newServiceTypeNumber = Number(newServiceType);
        if (
            newServiceTypeNumber !== ContractServiceType.Appeal &&
            newServiceTypeNumber !== ContractServiceType.Litigation &&
            newServiceTypeNumber !== ContractServiceType.LitigationOnly
        ) {
            term.propertyTypeId = null;
            term.contingencyCap = null;
            term.contingencyPct = null;
        }

        if (newServiceTypeNumber === ContractServiceType.Misc || newServiceTypeNumber === ContractServiceType.Hourly) {
            term.flatFee = null;
        }
    }

    async showComments(term: ContractTerm, readOnly: boolean): Promise<void> {
        const newRemarks = await this._commentsModalService.openAddCommentModal({
            title: 'Contract Term Remarks',
            commentBody: term.remarks,
            allowBlank: false,
            readOnly: readOnly,
            entityTypeName: 'company',
            entityId: this.companyId,
        });

        if (!readOnly && newRemarks !== undefined) {
            term.remarks = newRemarks;
        }
    }

    private async _confirmAutoSave(): Promise<boolean> {
        if (this._isPanelDirty()) {
            try {
                await this._messageModalService.open({
                    message: 'Current Contract Terms will be Saved',
                    buttons: MessageModalButtons.OKCancel,
                    displayMode: MessageModalDisplayMode.SingleMessage,
                });

                await this.savePanel(true);
            } catch {
                return false;
            }
        }

        return true;
    }

    private _panelIsInvalid(): string {
        const missingTerms: boolean = this.currentContractTerms.some((term: ContractTerm) => {
            return (
                !term.contingencyPct &&
                !term.flatFee &&
                !term.hourlyRate &&
                term.contractServiceTypeId !== ContractServiceType.Misc &&
                term.contractServiceTypeId !== ContractServiceType.Hourly
            );
        });

        if (missingTerms) {
            return 'At least one term is missing fee details.';
        }

        const negativeAmounts = this.currentContractTerms.some((term: ContractTerm) => {
            return [term.contingencyPct, term.contingencyCap, term.flatFee, term.hourlyRate].some((v) => v < 0);
        });

        if (negativeAmounts) {
            return 'Amounts cannot be negative';
        }

        const missingYear = this.currentContractTerms.some((term: ContractTerm) => {
            return term.taxYear === 0 || !term.taxYear;
        });

        if (missingYear) {
            return 'At least one term is missing a tax year.';
        }

        const invalidServiceYears = Object.values(
            this.currentContractTerms
                .filter((term: ContractTerm) => {
                    return (
                        term.contractServiceTypeId === ContractServiceType.Misc ||
                        term.contractServiceTypeId === ContractServiceType.Hourly
                    );
                })
                .reduce<Record<string, ContractTerm[]>>((grouped, term) => {
                    const key = `${term.contractServiceTypeId}-${term.taxYear}`;
                    if (!grouped[key]) {
                        grouped[key] = [];
                    }
                    grouped[key].push(term);
                    return grouped;
                }, {}),
        ).some((grouping) => grouping.length > 1);

        if (invalidServiceYears) {
            return 'Only one Misc contract term can be created for a given year';
        }

        const percentInvalid: boolean = this.currentContractTerms.some((term: ContractTerm) => {
            return term.contingencyPct < 0 || term.contingencyPct > 100;
        });

        return percentInvalid ? 'Contingency Fee must be between 0 and 100%' : '';
    }

    private _getCurrentTermsForSave(): ContractTerm[] {
        const currentContractTermsCopy = this.currentContractTerms.map((term: ContractTerm) => ({ ...term }));
        return currentContractTermsCopy.map((term: ContractTerm) => {
            if (term.contingencyPct) {
                term.contingencyPct = new Decimal(term.contingencyPct).dividedBy(100).toNumber();
            }

            if (!term.efAction) {
                const foundTerm: ContractTerm | undefined = this.allData.currentContractTerms.find(
                    (t: ContractTerm) => t.contractTermDetailId === term.contractTermDetailId,
                );
                if (foundTerm && !this._areContractTermsEqual(foundTerm, term)) {
                    term.efAction = 'update';
                } else {
                    term.efAction = null;
                }
            }

            return term;
        }) as ContractTerm[];
    }

    private _areContractTermsEqual(term1: ContractTerm, term2: ContractTerm): boolean {
        const keys1 = Object.keys(term1) as (keyof ContractTerm)[];
        const keys2 = Object.keys(term2) as (keyof ContractTerm)[];

        if (keys1.length !== keys2.length) {
            return false;
        }

        for (const key of keys1) {
            if (term1[key] !== term2[key]) {
                return false;
            }
        }

        return true;
    }

    private async _saveData(saveBody: any, stayInEditMode: boolean): Promise<void> {
        try {
            const result = await this._contractsInvoicesService.saveCompanyContractTerms(this.companyId, saveBody);

            this._populateContractTermData(result);

            this.isEditMode = !!stayInEditMode;
            if (!stayInEditMode) {
                this._navigationService.disableNavWarning();
            }
        } finally {
            this.isLoading = false;
        }
    }

    private _populateContractTermData(data: ContractTermData): void {
        this.allData = data;
        this.useTopLevelCompanyTerms = this.allData.useTopLevelCompanyTerms;

        this._populateYearsDropdown();
        this._setSelectedYear();
        this._getDataForYear();
    }

    private _populateYearsDropdown(): void {
        this.topLevelYears = this._getYears(this.allData.topLevelCompanyContractTerms);
        this.subsidiaryYears = this._getYears(this.allData.currentContractTerms);
    }

    private _setSelectedYear(): void {
        this.selectedYear = this.useTopLevelCompanyTerms
            ? this._getSelectedYear(this.topLevelYears)
            : this._getSelectedYear(this.subsidiaryYears);
    }

    private _getYears(terms: ContractTerm[]): number[] {
        const yearsSet = new Set<number>();
        for (const term of terms) {
            yearsSet.add(term.taxYear as number);
        }
        const years = Array.from(yearsSet);
        years.sort((a, b) => b - a);
        return years;
    }

    private _getSelectedYear(years: number[]): number {
        if (years.length) {
            const selectedYear: number = years.includes(this.selectedYear) ? this.selectedYear : null;
            const currentYear: number = years.includes(this.currentYear) ? this.currentYear : null;

            return selectedYear || currentYear || years.reduce((max, year) => (year > max ? year : max), years[0]);
        } else {
            return 0;
        }
    }

    private _getDataForYear(): void {
        this.selectedYear = Number(this.selectedYear);

        this.topLevelCompanyContractTerms = this._prepareDataForView(this.allData.topLevelCompanyContractTerms);
        this.currentContractTerms = this._prepareDataForView(this.allData.currentContractTerms);
    }

    private _prepareDataForView(contractTerms: ContractTerm[]): ContractTerm[] {
        const filtered = contractTerms.filter((term: ContractTerm) => {
            return this.selectedYear === 0 || term.taxYear == this.selectedYear;
        });

        const mapped = filtered.map((term: ContractTerm) => {
            const termCopy: ContractTerm = { ...term };
            if (termCopy.contingencyPct) {
                termCopy.contingencyPct = new Decimal(termCopy.contingencyPct).times(100).toNumber();
            }
            return termCopy;
        });

        mapped.sort((a, b) => (a.taxYear as number) - (b.taxYear as number));
        mapped.reverse();

        return mapped as ContractTerm[];
    }

    private async _launchAddYearModal(): Promise<void> {
        const params: AddTermYearModalParams = {
            companyId: this.companyId,
            selectedYear: this.selectedYear,
            allData: this.allData,
            services: this.services,
            currentContractTerms: this.currentContractTerms,
        };
        const result = await this._weissmanModalService.showAsync(AddTermYearComponent, params, 'modal-lg');

        if (!result) {
            return;
        }

        this._populateYearsDropdown();
        this.selectedYear = result;

        this._getDataForYear();
        this.isEditMode = false;
        this._navigationService.disableNavWarning();
    }

    private async _launchConfigureFeeEscalatorModal(): Promise<void> {
        const params: ConfigureFeeEscalatorModalParams = {
            allData: this.allData,
            companyId: this.companyId,
            services: this.services,
        };

        const saved = await this._weissmanModalService.showAsync(ConfigureFeeEscalatorComponent, params, 'modal-lg');

        if (!saved) {
            return;
        }

        const result = await this._contractsInvoicesService.getContractTermsByCompany(this.companyId);

        this._populateContractTermData(result);
    }

    private _isPanelDirty(): boolean {
        return this.useTopLevelCompanyTerms !== this.allData.useTopLevelCompanyTerms || this._areTermsDirty();
    }

    private _areTermsDirty(): boolean {
        return !this.useTopLevelCompanyTerms && this._aContractTermHasChanged();
    }

    private _aContractTermHasChanged(): boolean {
        return this.currentContractTerms.some((term: ContractTerm) => {
            if (term.efAction) {
                return true;
            }

            const copiedTerm: ContractTerm = { ...term } as ContractTerm;
            if (copiedTerm.contingencyPct) {
                copiedTerm.contingencyPct = new Decimal(copiedTerm.contingencyPct).dividedBy(100).toNumber();
            }
            const foundTerm: ContractTerm | undefined = this.allData.currentContractTerms.find(
                (t: ContractTerm) => t.contractTermDetailId === copiedTerm.contractTermDetailId,
            );

            if (!foundTerm) {
                return true;
            }

            return !this._areContractTermsEqual(foundTerm, copiedTerm) as boolean;
        });
    }
}
